// src/components/common/CollapsibleSection.jsx
import React, { useState, useId, useRef, useEffect } from 'react';
import { Box, Stack, Typography, Collapse, IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * CollapsibleSection — header + collapsible body.
 *
 * @description
 *   Reduces visual density on dashboard and detail pages by hiding
 *   secondary content behind a chevron-toggle header. The trigger is
 *   keyboard-accessible (role="button", aria-expanded, aria-controls).
 *
 * @useWhen
 *   - Group of related but secondary content under a heading
 *   - Optional content the user can opt-in to view (per Anti-Pattern #11)
 *   - Title-with-action header (e.g. "Pinned facts (3)")
 *   - Folds inside dense list rows (e.g. a card's rarely-needed details)
 *
 * @dontUseFor
 *   - Always-visible primary content — render inline instead
 *   - Modal/dialog content — use Dialog
 *
 * @related
 *   - Accordion (MUI) — for multi-section toggle groups
 *
 * @props
 *   title            ReactNode     Section heading. A node, not only a string:
 *                                  the caller may append a short `caption` hint
 *                                  inside the same heading (Operations uses it for
 *                                  „Month close · Export proofs: 2 open“). The
 *                                  node must not render a `<p>` — a block element
 *                                  inside the `<h6>` breaks the heading; use
 *                                  `component="span"` (precedent: ProductDetail).
 *   ariaLabel        string?       Accessible name for the toggle. Needed when a
 *                                  page renders SEVERAL of these: six folds all
 *                                  announced as „Details" are indistinguishable
 *                                  to a screen reader (Regel-16-Warnung W1,
 *                                  2026-10-01). Falls back to `title`.
 *   titleVariant     string?       MUI variant for the heading (default: 'h6').
 *                                  Added 2026-10-01: inside a dense card the h6
 *                                  became the LOUDEST text on the row while
 *                                  carrying the least important content. The
 *                                  caller decides the weight; 'h6' stays the
 *                                  default so no existing call site moves.
 *   defaultExpanded  boolean       Initial state (default: true)
 *   children         ReactNode
 *   action           ReactNode?    Optional action rendered in the header row
 *   summary          string?       Short content hint, right-aligned before the
 *                                  chevron (e.g. „12 SKUs · 4 vendors"). A fold
 *                                  with nothing but a title and a chevron gives
 *                                  no clue what is behind it — the hint belongs
 *                                  in the header, not smuggled into `title`,
 *                                  where it renders at heading weight.
 *                                  Added 2026-10-06.
 *                                  🔴 Since 2026-10-06 (R16 W2) it SHRINKS:
 *                                  title and hint both carry `minWidth: 0`, the
 *                                  hint is a one-liner with an ellipsis and is
 *                                  hidden below `sm`. Before that the sum of
 *                                  the two minimum widths plus the chevron
 *                                  could exceed the row on a narrow screen —
 *                                  the title did not shorten, the row ran over.
 *   sx               object?       Extra styles on the outer Box
 *   tooltip          string?       German term for the English heading, shown on
 *                                  the FOCUSABLE header (Welle 3 PR 3, R16 W1).
 *                                  `describeChild` keeps the visible title as the
 *                                  accessible NAME and makes the tooltip the
 *                                  description — a `title` attribute on the
 *                                  section would be mouse-only.
 *
 * 🔴 `SECTION_EXPAND_EVENT` (R16 W5 / R15 W3, 01.11.2026): `Collapse` haelt die
 *    Kinder im DOM (kein `unmountOnExit`), aber mit Hoehe 0. Ein Sprung auf einen
 *    Anker in einem ZUGEKLAPPTEN Abschnitt landete damit im Nichts — sichtbar
 *    passierte nichts. Wer springen will, schickt dieses Ereignis mit dem Ziel
 *    als `detail.ziel`; die Sektion klappt auf, wenn das Ziel in ihr liegt, und
 *    meldet das ueber `preventDefault()`. Der Aufrufer wartet dann die
 *    Collapse-Dauer ab, bevor er misst.
 */
/** Ereignisname fuer „klapp den Abschnitt auf, in dem dieses Ziel liegt". */
export const SECTION_EXPAND_EVENT = 'pog:section-expand';

export default function CollapsibleSection({
  title,
  ariaLabel,
  titleVariant = 'h6',
  defaultExpanded = true,
  children,
  action = null,
  summary = null,
  tooltip = null,
  sx,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const reactId = useId();
  const panelId = `collapsible-${reactId}`;
  const panelRef = useRef(null);

  // 🔴 R16 W5 / R15 W3 (01.11.2026): ein Sprung auf einen Anker in dieser
  //    Sektion klappt sie auf. Ohne das landet er am eingeklappten Kopf, und
  //    der Mensch sieht nichts — das Ziel ist da, aber unsichtbar.
  //    🔴 R15 W2 / R16 W1 (Schlussrunde, 2026-11-02): `preventDefault()` lief
  //    AUCH bei bereits offener Sektion. `scrollToSection` liest das als „war
  //    zugeklappt" und wartete 300 ms, ohne dass etwas aufklappte — auf dem
  //    standardmaessig offenen „Logistics" also bei jedem Sprung. Gemeldet wird
  //    nur, wenn sich wirklich etwas bewegt.
  useEffect(() => {
    const aufklappen = (e) => {
      if (!panelRef.current || !e.detail?.ziel) return;
      if (!panelRef.current.contains(e.detail.ziel)) return;
      if (expanded) return;    // schon offen: nichts zu tun, nicht abbrechen
      e.preventDefault();      // meldet dem Aufrufer: hier hat sich etwas bewegt
      setExpanded(true);
    };
    document.addEventListener(SECTION_EXPAND_EVENT, aufklappen);
    return () => document.removeEventListener(SECTION_EXPAND_EVENT, aufklappen);
  }, [expanded]);

  const toggle = () => setExpanded((prev) => !prev);

  const kopf = (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel || undefined}
      aria-expanded={expanded}
      aria-controls={panelId}
      sx={{ cursor: 'pointer', userSelect: 'none', mb: expanded ? 1.5 : 0 }}
    >
        {/* 🔴 `1 1 auto`, nicht `flex: 1` (= `1 1 0%`): mit der Basis 0 hat der
            Titel im Schrumpf-Fall kein Gewicht und fiele auf Breite 0 zusammen,
            waehrend der Hinweis alles nimmt. Mit der Inhaltsbreite als Basis
            schrumpfen beide im Verhaeltnis — und bei freiem Platz waechst der
            Titel wie vorher ueber die ganze Restbreite. */}
        <Typography variant={titleVariant} sx={{ flex: '1 1 auto', minWidth: 0 }}>
          {title}
        </Typography>
        {action && (
          <Box onClick={(e) => e.stopPropagation()}>
            {action}
          </Box>
        )}
        {summary && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              // R16 W2: der Hinweis darf SCHRUMPFEN, statt die Zeile zu
              // sprengen. Einzeiler mit Ellipse; unter `sm` ganz aus, weil er
              // dort auf zwei Zeichen zusammenfiele und nichts mehr sagt.
              display: { xs: 'none', sm: 'block' },
              flexShrink: 1,
              minWidth: 0,
              maxWidth: '50%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            {summary}
          </Typography>
        )}
        <IconButton
          size="small"
          tabIndex={-1}
          aria-hidden="true"
          sx={{
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s',
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
    </Stack>
  );

  return (
    <Box sx={sx}>
      {tooltip ? (
        // `describeChild`: der sichtbare Titel bleibt der NAME, der deutsche
        // Begriff wird zur BESCHREIBUNG. Der Kopf ist schon fokussierbar
        // (`role="button"` + `tabIndex={0}`), also haengt der Tooltip daran und
        // nicht an einem zweiten Fokuspunkt daneben.
        <Tooltip title={tooltip} describeChild>{kopf}</Tooltip>
      ) : kopf}
      <Collapse in={expanded} timeout={250}>
        <div id={panelId} ref={panelRef}>{children}</div>
      </Collapse>
    </Box>
  );
}
